import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { MapLayer } from 'react-leaflet'
import { inject, observer } from 'mobx-react'
import L from 'leaflet'
import _ from 'lodash'
import 'leaflet.vectorgrid'
import { pointInGeoJSON, getVectorTileEndpoint } from '../../utils'
import Icon from 'components/common/Icon'
import { getToken } from '../../token'

const MAX_NATIVE_ZOOM = 8
const BASE_BOUNDARY_WEIGHT = 0.5
const ACTIVE_BOUNDARY_WEIGHT = 0.9

// Past MAX_NATIVE_ZOOM Leaflet reuses the zoom-8 vector tiles and CSS-scales
// them by 2^(zoom - MAX_NATIVE_ZOOM), which inflates stroke widths with every
// extra zoom level. Divide the weight by that scale factor so the on-screen
// border thickness only grows slightly with zoom instead of doubling.
const getZoomCompensatedWeight = (zoom, baseWeight) => {
  const overzoom = Math.max(0, zoom - MAX_NATIVE_ZOOM)
  if (overzoom === 0) return baseWeight
  const onScreenWeight = Math.min(baseWeight + (overzoom * 0.15), baseWeight + 1.5)
  return onScreenWeight / Math.pow(2, overzoom)
}

const VECTOR_TILE_LAYER_STYLE = (weight) => ({
  color: '#ff5500',
  weight,
  fill: true,
  fillColor: 'blue',
  fillOpacity: 0.6,
})

const HIDEN_VECTOR_TILE_LAYER_STYLE = {
  weight: 0,
  fill: false,
  fillOpacity: 0,
  opacity: 0
}
const SELECTED_VECTOR_TILE_LAYER_STYLE = (weight) => ({
  weight,
  cursor: 'pointer',
  // Slightly brighten the boundary stroke for selected features.
  color: '#ff9b66',
  fill: true,
  // Keep the original hue but blend a little toward white to improve adjacency contrast.
  fillOpacity: 0.82,
  fillColor: '#b8ddff',
})

const HOVER_VECTOR_TILE_LAYER_STYLE = (weight) => ({
  weight,
  cursor: 'pointer',
  color: '#ff5500',
  fill: true,
  fillOpacity: 0.85,
  fillColor: 'rgba(255, 255, 255, 0.85)',
})

const isBoundarySelected = (selectedBoundaries = [], featureId) => (
  selectedBoundaries.some(boundary => String(boundary.remote_db_id || boundary.id) === String(featureId))
)

// leaflet.vectorgrid renders into the default GridLayer pane (tilePane, z-index 200).
// A layer's own `zIndex` option only reorders siblings within its own pane, so no
// zIndex value can lift a tilePane layer above shapes (overlayPane, 400) or points
// (markerPane, 600) -- those panes are separate stacking contexts with a higher
// base z-index. Render into a dedicated pane above both instead.
const VECTOR_TILE_PANE = 'vectorTileLayerPane'
const VECTOR_TILE_PANE_Z_INDEX = 620

class VectorTileLayer extends MapLayer {
  highlightedFeatureId = null
  activePopup = null
  lastSelectedIds = new Set()
  // Debouncing ensures the reset style only fires after the mouse has settled off the feature.
  constructor (props) {
    super(props)
    this.resetHighlight = (layer, featureId) => {
      if (featureId === this.highlightedFeatureId) {
        try {
          // unmount any rendered React icon in popup
          layer.resetFeatureStyle(featureId)
          if (this.activePopup) {
            try { this.activePopup.remove() } catch (e) { }
            this.activePopup = null
          }
        } catch (err) {
          console.error('Error resetting feature style:', err)
        }
        if (layer && layer._map) layer._map.closePopup()
        this.highlightedFeatureId = null
      }
    }
    this.debouncedMouseOut = _.debounce(this.resetHighlight, 50) // 50ms is standard for stability
  }

  componentDidMount () {
    super.componentDidMount()
    const { selectionStore } = this.props
    if (selectionStore && selectionStore.item && selectionStore.item.selectedBoundaries) {
      this.lastSelectedIds = new Set(selectionStore.item.selectedBoundaries.map(b => b.remote_db_id || b.id))
    }
  }

  componentDidUpdate (prevProps) {
    super.componentDidUpdate(prevProps)
    const currSelectionStore = this.props.selectionStore

    if (!currSelectionStore || !currSelectionStore.item) return

    const currBoundaries = currSelectionStore.item.selectedBoundaries || []
    const currIds = new Set(currBoundaries.map(b => b.remote_db_id || b.id))

    // Use stored previous IDs
    const prevIds = this.lastSelectedIds || new Set()

    if (_.isEqual(prevIds, currIds)) return

    // Find changed IDs
    const changedIds = []

    prevIds.forEach(id => {
      if (!currIds.has(id)) changedIds.push(id)
    })
    currIds.forEach(id => {
      if (!prevIds.has(id)) changedIds.push(id)
    })

    // Update stored IDs
    this.lastSelectedIds = currIds

    // Reset styles for changed IDs (this forces re-evaluation of the style function)
    if (this.leafletElement) {
      changedIds.forEach(id => {
        this.leafletElement.resetFeatureStyle(id)
      })
    }
  }

  createLeafletElement (props) {
    const { selectionStore } = props || this.props
    const { useZipCodes } = selectionStore.item
    const selectedBoundary = selectionStore.item.selectedBoundary
    const { ids: boundaryIds = [], zip_code_admin_level, is_zip_code } = selectedBoundary
    const fetch_zips = Boolean((useZipCodes && boundaryIds.length >= (zip_code_admin_level || 0)) || is_zip_code)
    const url = getVectorTileEndpoint(boundaryIds.length, boundaryIds[0] || -999, boundaryIds[1] || -999, fetch_zips)
    let layer = null

    const map = this.context.map
    if (map && !map.getPane(VECTOR_TILE_PANE)) {
      map.createPane(VECTOR_TILE_PANE).style.zIndex = VECTOR_TILE_PANE_Z_INDEX
    }
    // The zoom passed to the style callbacks is the tile zoom, which is
    // clamped to MAX_NATIVE_ZOOM, so read the real zoom from the map instead.
    const getWeight = (baseWeight) => {
      const map = layer && layer._map
      const zoom = map ? map.getZoom() : MAX_NATIVE_ZOOM
      return getZoomCompensatedWeight(zoom, baseWeight)
    }
    const options = {
      fetchOptions: {
        headers: {
          Authorization: `Token ${getToken()}`
        }
      },
      tms: false,
      maxNativeZoom: MAX_NATIVE_ZOOM,
      maxZoom: 18,
      interactive: true,
      pane: VECTOR_TILE_PANE,
      zIndex: 99999,
      // REQUIRED: Tell the plugin which attribute holds the unique ID.
      getFeatureId: function (feature) {
        return feature.properties.id
      },
      // use the name of the layer in the styles.
      vectorTileLayerStyles: {
        admin_layer: (properties, zoom) => {
          const check = (boundaryIds || []).map((id, index) => {
            return String(properties[`ID_${index}`]) === String(id)
          })

          if (check.every(v => v === true)) {
            const selectedBoundaries = (selectionStore.item && selectionStore.item.selectedBoundaries) || []
            if (isBoundarySelected(selectedBoundaries, properties.id)) {
              return SELECTED_VECTOR_TILE_LAYER_STYLE(getWeight(ACTIVE_BOUNDARY_WEIGHT))
            }
            return VECTOR_TILE_LAYER_STYLE(getWeight(BASE_BOUNDARY_WEIGHT))
          }
          return HIDEN_VECTOR_TILE_LAYER_STYLE
        },
        zip_code_boundaries: (properties, zoom) => {
          const selectedBoundaries = (selectionStore.item && selectionStore.item.selectedBoundaries) || []
          if (isBoundarySelected(selectedBoundaries, properties.id)) {
            return SELECTED_VECTOR_TILE_LAYER_STYLE(getWeight(ACTIVE_BOUNDARY_WEIGHT))
          }
          return VECTOR_TILE_LAYER_STYLE(getWeight(BASE_BOUNDARY_WEIGHT))
        },
        // Define a custom highlight style. Kept as a function so the weight
        // is re-evaluated at the zoom level current when it gets applied.
        highlight: () => HOVER_VECTOR_TILE_LAYER_STYLE(getWeight(ACTIVE_BOUNDARY_WEIGHT)),
      },
    }

    layer = L.vectorGrid.protobuf(url, options)

    // Overzoomed tiles are not re-rendered on zoom, so re-run the style
    // functions for every feature in the loaded tiles to apply the
    // zoom-compensated border weight. This restyles in place, no refetch.
    const restyleLoadedFeatures = () => {
      const tiles = (layer && layer._vectorTiles) || {}
      const featureIds = new Set()
      Object.keys(tiles).forEach((tileKey) => {
        const features = (tiles[tileKey] && tiles[tileKey]._features) || {}
        Object.keys(features).forEach(id => featureIds.add(id))
      })
      featureIds.forEach((id) => {
        if (String(id) === String(this.highlightedFeatureId)) {
          // Re-apply the hover style instead of resetting it away.
          layer.setFeatureStyle(this.highlightedFeatureId, options.vectorTileLayerStyles.highlight)
        } else {
          layer.resetFeatureStyle(id)
        }
      })
    }
    let attachedMap = null
    layer.on('add', () => {
      attachedMap = layer._map
      if (attachedMap) attachedMap.on('zoomend', restyleLoadedFeatures)
    })
    layer.on('remove', () => {
      if (attachedMap) {
        attachedMap.off('zoomend', restyleLoadedFeatures)
        attachedMap = null
      }
    })
    // Mouse click event on feature
    layer.on('click', (e) => {
      const target = (e && e.layer) || (e && e.target)
      const props = target && target.properties
      if (!props) return

      const currSelectionStore = this.props.selectionStore
      if (!currSelectionStore || !currSelectionStore.item) return

      const currentSelectedBoundary = currSelectionStore.item.selectedBoundary
      const currentBoundaries = (currentSelectedBoundary && currentSelectedBoundary.boundaries) || []
      const boundary = currentBoundaries.find(b => String(b.id) === String(props.id))

      const boundaryId = boundary ? boundary.id : props.id
      const boundaryName = (boundary && boundary.name) || props.name || ''
      const targetGroupId = currSelectionStore.data && currSelectionStore.data.group ? currSelectionStore.data.group.pk : null
      const isZip = boundary ? boundary.is_zip_code : Boolean(fetch_zips || (currentSelectedBoundary && currentSelectedBoundary.is_zip_code))

      let ids = boundary ? boundary.ids : []
      if (!ids || ids.length === 0) {
        if (currentSelectedBoundary && Array.isArray(currentSelectedBoundary.ids)) {
          ids = [...currentSelectedBoundary.ids, props.id]
        } else {
          ids = [props.id]
        }
      }

      const selectedBoundaries = currSelectionStore.item.selectedBoundaries || []
      const isSelected = selectedBoundaries.some(b => String(b.remote_db_id || b.id) === String(boundaryId))
      if (isSelected) {
        if (typeof currSelectionStore.item.removeBoundaryFromSelection === 'function') {
          currSelectionStore.item.removeBoundaryFromSelection({ id: boundaryId })
        }
      } else {
        if (typeof currSelectionStore.item.addBoundaryToSelection === 'function') {
          currSelectionStore.item.addBoundaryToSelection({
            id: boundaryId,
            name: boundaryName,
            targetGroupId,
            ids,
            isZipCode: isZip
          })
        }
      }
    })
    // Mouse hover event on feature
    layer.on('mouseover', (e) => {
      const target = (e && e.layer) || (e && e.target)
      const props = target && target.properties
      if (!props) return
      const featureId = props.id
      const map = layer._map || (this.props && this.props.leaflet && this.props.leaflet.map)

      if (this.highlightedFeatureId === featureId) return
      // this.debouncedMouseOut.cancel()
      // If it's a *DIFFERENT* feature, reset the old one immediately.
      if (featureId !== this.highlightedFeatureId) {
        layer.resetFeatureStyle(this.highlightedFeatureId)
        if (this.activePopup) {
          try { this.activePopup.remove() } catch (e) { }
          this.activePopup = null
        }
      }
      layer.setFeatureStyle(featureId, options.vectorTileLayerStyles.highlight)
      this.highlightedFeatureId = featureId
      const content = `${props.name || ''}`
      if (map) {
        const popupLatLng = map.getCenter()
        const icon = ReactDOMServer.renderToString(React.createElement(Icon, { extraClasses: ['item-list__icon'], id: 'hexagon' }))
        const popup = L.popup({ closeOnClick: false, className: 'admin-boundary-tile-popup' })
          .setLatLng(e.latlng || popupLatLng)
          .setContent(`<div class='admin-boundary-tile-popup-container'>
            <div>${icon}</div>
            <div>${content}</div>
          </div>`)
          .openOn(map)
        this.activePopup = popup
      }
    })

    // Mouse out event on feature
    layer.on('mouseout', (e) => {
      const props = e.layer && e.layer.properties
      if (!props) return
      const latlng = e.latlng
      if (selectionStore.item && selectionStore.item.selectedBoundary && selectionStore.item.selectedBoundary.geoJSON) {
        const isPointInBoundary = pointInGeoJSON([latlng.lng, latlng.lat], selectionStore.item.selectedBoundary.geoJSON)
        if (isPointInBoundary) {
          return
        }
      }
      this.debouncedMouseOut(layer, props.id)
    })

    return layer
  }

  // updateLeafletElement (fromProps, toProps) {
  //   const prevIds = fromProps.selectionStore.item.selectedBoundary.id
  //   const nextIds = toProps.selectionStore.item.selectedBoundary.id
  //   console.log('VectorTileLayer updateLeafletElement called', prevIds, nextIds)
  //   // Check if the state that builds the URL has changed
  //   if (!_.isEqual(prevIds, nextIds)) {
  //   // This is a "hack" for MapLayer:
  //   // We manually trigger the layer removal and re-addition
  //   // by accessing the leaflet map instance.
  //     this.layerContainer.removeLayer(this.leafletElement)

  //     // Create the new element and assign it to the class property
  //     this.leafletElement = this.createLeafletElement(toProps)
  //     this.layerContainer.addLayer(this.leafletElement)
  //   }
  // }
}

export default inject('selectionStore')(
  observer(VectorTileLayer)
)
